/**
 * Smooth ocean-like height field driven by music bands (+ optional soft waveform).
 * Looks like rolling waves, not a raw frequency plot.
 */
export class OceanWave {
  private heights: Float32Array;
  private smooth: Float32Array;
  private phase = 0;
  private readonly segs: number;

  constructor(segs: number) {
    this.segs = segs;
    this.heights = new Float32Array(segs + 1);
    this.smooth = new Float32Array(segs + 1);
  }

  /**
   * Advance simulation.
   * @returns heights[0..segs] world-ish units
   */
  tick(
    dt: number,
    energy: number,
    bass: number,
    kick: number,
    waveform: Float32Array,
  ): Float32Array {
    this.phase += dt * (0.55 + energy * 0.9);

    const n = this.segs;
    for (let i = 0; i <= n; i++) {
      const u = i / n;
      const a1 = 0.55 + bass * 1.1;
      const a2 = 0.28 + energy * 0.55;
      const a3 = 0.12 + kick * 0.25;
      let h =
        Math.sin(u * Math.PI * 2 * 1.15 + this.phase * 1.1) * a1 +
        Math.sin(u * Math.PI * 2 * 2.4 - this.phase * 1.7) * a2 +
        Math.sin(u * Math.PI * 2 * 0.55 + this.phase * 0.45) * a3;

      if (waveform.length > 4) {
        const idx = Math.floor(u * (waveform.length - 1));
        let s = 0;
        let c = 0;
        for (let k = -4; k <= 4; k++) {
          const j = Math.max(0, Math.min(waveform.length - 1, idx + k));
          s += waveform[j];
          c++;
        }
        h += (s / c) * (0.35 + energy * 0.45);
      }
      this.heights[i] = h;
    }

    for (let pass = 0; pass < 2; pass++) {
      for (let i = 0; i <= n; i++) {
        const l = this.heights[Math.max(0, i - 1)];
        const c = this.heights[i];
        const r = this.heights[Math.min(n, i + 1)];
        this.smooth[i] = l * 0.25 + c * 0.5 + r * 0.25;
      }
      this.heights.set(this.smooth);
    }

    for (let i = 0; i <= n; i++) {
      this.smooth[i] = this.smooth[i] * 0.35 + this.heights[i] * 0.65;
    }
    this.heights.set(this.smooth);
    return this.heights;
  }

  sampleAt(u: number): number {
    const x = Math.max(0, Math.min(1, u)) * this.segs;
    const i0 = Math.floor(x);
    const i1 = Math.min(this.segs, i0 + 1);
    const f = x - i0;
    return this.heights[i0] * (1 - f) + this.heights[i1] * f;
  }
}
